import { useCallback, useEffect, useMemo, useState } from "react"
import { Navigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Checkbox } from "@/components/ui/checkbox"
import { Badge } from "@/components/ui/badge"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { getEmployeeTypeList } from "@/api/employee"
import { getUsers, updateUsers, deleteUsers } from "@/api/user"
import { getShopCustomerGroup } from "@/api/shop"
import { useAuthStore } from "@/store/auth"
import { useShopStore } from "@/store/shop"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import type { User, UsersUpdate } from "@/types/user"
import type { EmployeeType } from "@/types/employee"
import type { CustomerGroup } from "@/types/shop"
import { ROUTES } from "@/config/routes"
import { toast } from "sonner"
import { Loader2, ChevronLeft, ChevronRight, MoreHorizontal } from "lucide-react"

const PAGE_SIZE_OPTIONS = ["10", "20", "30", "50"] as const

export function UsersManagementPage() {
  const authUser = useAuthStore((state) => state.user)
  const authLoading = useAuthStore((state) => state.isLoading)
  const { selectedShop } = useShopStore()
  const [users, setUsers] = useState<User[]>([])
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isActionLoading, setIsActionLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [superAdminCount, setSuperAdminCount] = useState(0)
  const [employeeTypes, setEmployeeTypes] = useState<EmployeeType[]>([])
  const [customerGroups, setCustomerGroups] = useState<CustomerGroup[]>([])
  const [approvalEmployeeTypeId, setApprovalEmployeeTypeId] = useState<string>("")
  const [approvalCustomerGroupNo, setApprovalCustomerGroupNo] = useState<string>("")
  const [approvalEmployeeIsFixedGroup, setApprovalEmployeeIsFixedGroup] = useState<"T" | "F" | "">("")
  const [approvalEmployeeBaseField, setApprovalEmployeeBaseField] = useState<"" | "email" | "phoneNumber">("")
  const [approvalResignGroupNo, setApprovalResignGroupNo] = useState<string>("")
  const [approvalResignIsFixedGroup, setApprovalResignIsFixedGroup] = useState<"T" | "F" | "">("")
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false)
  const selfUserId = authUser?.id ?? null
  const isSuperAdmin = authUser?.isSuperAdmin === true
  const isCafe24User = authUser?.platform === "cafe24"
  const shopNo = isCafe24User && selectedShop ? selectedShop.shopNo : 1

  const employeeTypeMap = useMemo(() => {
    const map = new Map<number, string>()
    employeeTypes.forEach((type) => map.set(type.typeId, type.typeName))
    return map
  }, [employeeTypes])

  const customerGroupMap = useMemo(() => {
    const map = new Map<number, string>()
    customerGroups.forEach((group) => map.set(group.groupNo, group.groupName))
    return map
  }, [customerGroups])

  const selectedSuperAdminCount = useMemo(() => {
    if (selectedUserIds.length === 0) return 0
    return users.filter((user) => user.isSuperAdmin && selectedUserIds.includes(user.id)).length
  }, [users, selectedUserIds])

  const manageableUsers = useMemo(
    () => (selfUserId ? users.filter((user) => user.id !== selfUserId) : users),
    [users, selfUserId]
  )

  const isAllSelected = manageableUsers.length > 0 && selectedUserIds.length === manageableUsers.length
  const isIndeterminate = selectedUserIds.length > 0 && selectedUserIds.length < manageableUsers.length

  const fetchUserList = useCallback(async (targetPage: number, targetLimit: number) => {
    setIsLoading(true)
    try {
      const response = await getUsers({ page: targetPage, limit: targetLimit })
      if (response.code === 200 && response.data) {
        const fetchedUsers = response.data.users ?? []
        const fetchedTotal = response.data.total ?? fetchedUsers.length
        const fetchedTotalPages = Math.max(response.data.totalPages ?? 1, 1)
        setUsers(fetchedUsers)
        setTotalCount(fetchedTotal)
        setTotalPages(fetchedTotalPages)
        setSuperAdminCount(response.data.superAdminCount ?? 0)
        if (targetPage > fetchedTotalPages) {
          setPage(fetchedTotalPages)
        }
      } else {
        toast.error(response.message || "사용자 목록을 불러오지 못했습니다.")
        setUsers([])
        setTotalCount(0)
        setTotalPages(1)
        setSuperAdminCount(0)
        if (targetPage !== 1) {
          setPage(1)
        }
      }
      setSelectedUserIds([])
    } catch (error) {
      console.error("사용자 목록 조회 오류:", error)
      toast.error("사용자 목록을 불러오는 중 오류가 발생했습니다.")
      setUsers([])
      setTotalCount(0)
      setTotalPages(1)
      setSuperAdminCount(0)
      if (targetPage !== 1) {
        setPage(1)
      }
    } finally {
      setIsLoading(false)
    }
  }, [])

  const fetchEmployeeMetadata = useCallback(async (targetShopNo: number) => {
    try {
      const [typeResponse, groupResponse] = await Promise.all([
        getEmployeeTypeList({ shopNo: targetShopNo }),
        getShopCustomerGroup({ shopNo: targetShopNo }),
      ])

      if (typeResponse.code === 200 && typeResponse.data) {
        const types = typeResponse.data.types ?? []
        setEmployeeTypes(types)
        setApprovalEmployeeTypeId((prev) => (prev && types.some((type) => type.typeId.toString() === prev) ? prev : ""))
      } else {
        setEmployeeTypes([])
        setApprovalEmployeeTypeId("")
        toast.error(typeResponse.message || "임직원 구분을 불러오지 못했습니다.")
      }

      if (groupResponse.code === 200 && groupResponse.data) {
        setCustomerGroups(groupResponse.data)
        setApprovalCustomerGroupNo((prev) =>
          prev && groupResponse.data.some((group) => group.groupNo.toString() === prev) ? prev : ""
        )
      } else {
        setCustomerGroups([])
        setApprovalCustomerGroupNo("")
        toast.error(groupResponse.message || "회원 등급 정보를 불러오지 못했습니다.")
      }
    } catch (error) {
      console.error("임직원 메타데이터 조회 오류:", error)
      setEmployeeTypes([])
      setCustomerGroups([])
      setApprovalEmployeeTypeId("")
      setApprovalCustomerGroupNo("")
      toast.error("임직원 메타데이터를 불러오는 중 오류가 발생했습니다.")
    }
  }, [])

  const refreshCurrentPage = useCallback(async () => {
    await fetchUserList(page, pageSize)
  }, [fetchUserList, page, pageSize])

  useEffect(() => {
    if (!isSuperAdmin) return
    refreshCurrentPage()
  }, [isSuperAdmin, refreshCurrentPage])

  useEffect(() => {
    if (!isSuperAdmin) return
    fetchEmployeeMetadata(shopNo)
  }, [fetchEmployeeMetadata, isSuperAdmin, shopNo])

  const resetApprovalForm = useCallback(() => {
    setApprovalEmployeeTypeId("")
    setApprovalCustomerGroupNo("")
    setApprovalEmployeeIsFixedGroup("")
    setApprovalEmployeeBaseField("")
    setApprovalResignGroupNo("")
    setApprovalResignIsFixedGroup("")
  }, [])

  const ensureSelection = useCallback((targetUserIds?: number[]) => {
    const ids = targetUserIds ?? selectedUserIds
    if (ids.length === 0) {
      toast.error("계정을 선택해주세요.")
      return false
    }
    if (selfUserId && ids.includes(selfUserId)) {
      toast.error("본인 계정은 수정할 수 없습니다.")
      return false
    }
    return true
  }, [selectedUserIds, selfUserId])

  const performApprovalChange = useCallback(async (targetUserIds: number[], approved: boolean, options?: {
    employeeTypeId?: number
    customerGroupNo?: number
    employeeIsFixedGroup?: "T" | "F"
    employeeBaseField?: "email" | "phoneNumber"
    resignGroupNo?: number
    resignIsFixedGroup?: "T" | "F"
  }): Promise<boolean> => {
    if (targetUserIds.length === 0) return false

    const filteredUserIds = selfUserId
      ? targetUserIds.filter((userId) => userId !== selfUserId)
      : targetUserIds

    if (filteredUserIds.length === 0) {
      toast.error("본인 계정은 수정할 수 없습니다.")
      return false
    }

    if (filteredUserIds.length !== targetUserIds.length) {
      toast.error("선택한 계정 중 본인 계정은 수정할 수 없습니다.")
    }

    let isSuccess = false
    setIsActionLoading(true)
    try {
      const payload: UsersUpdate = {
        users: filteredUserIds.map((userId) => ({
          userId,
          isApproved: approved ? "T" : "F",
          ...(approved && options?.employeeTypeId !== undefined
            ? { allowedEmployeeTypeId: options.employeeTypeId }
            : {}),
          ...(approved && options?.customerGroupNo !== undefined
            ? { allowedEmployeeGroupNo: options.customerGroupNo }
            : {}),
          ...(approved && options?.employeeIsFixedGroup !== undefined
            ? { allowedEmployeeIsFixedGroup: options.employeeIsFixedGroup }
            : {}),
          ...(approved && options?.employeeBaseField !== undefined
            ? { allowedEmployeeBaseField: options.employeeBaseField }
            : {}),
          ...(approved && options?.resignGroupNo !== undefined
            ? { allowedResignGroupNo: options.resignGroupNo }
            : {}),
          ...(approved && options?.resignIsFixedGroup !== undefined
            ? { allowedResignIsFixedGroup: options.resignIsFixedGroup }
            : {}),
        })),
      }
      const response = await updateUsers(payload)
      if (response.code === 200) {
        toast.success(`선택한 ${filteredUserIds.length}개 계정이 ${approved ? "등록" : "미승인"} 처리되었습니다.`)
        await refreshCurrentPage()
        isSuccess = true
      } else {
        toast.error(response.message || "계정 상태 변경에 실패했습니다.")
      }
    } catch (error) {
      console.error("계정 상태 변경 오류:", error)
      toast.error("계정 상태 변경 중 오류가 발생했습니다.")
    } finally {
      setIsActionLoading(false)
    }
    return isSuccess
  }, [refreshCurrentPage, selfUserId])

  const handleApprovalChange = useCallback(async (approved: boolean) => {
    if (!ensureSelection()) return
    if (approved) {
      if (!approvalEmployeeTypeId || !approvalCustomerGroupNo) {
        toast.error("승인 시 관리 구분과 임직원 회원 등급을 선택해주세요.")
        return
      }
      if (!approvalEmployeeIsFixedGroup) {
        toast.error("임직원 회원 등급 고정 여부를 선택해주세요.")
        return
      }
      if (!approvalEmployeeBaseField) {
        toast.error("임직원 회원 정보 매칭 항목을 선택해주세요.")
        return
      }
      if (!approvalResignGroupNo) {
        toast.error("일반 회원 등급(퇴사 처리)을 선택해주세요.")
        return
      }
      if (!approvalResignIsFixedGroup) {
        toast.error("일반 회원 등급 고정 여부를 선택해주세요.")
        return
      }
    }

    const employeeTypeId = approvalEmployeeTypeId ? Number(approvalEmployeeTypeId) : undefined
    const customerGroupNo = approvalCustomerGroupNo ? Number(approvalCustomerGroupNo) : undefined
    const employeeIsFixedGroup = approvalEmployeeIsFixedGroup === "" ? undefined : (approvalEmployeeIsFixedGroup as "T" | "F")
    const employeeBaseField = approvalEmployeeBaseField === "" ? undefined : (approvalEmployeeBaseField as "email" | "phoneNumber")
    const resignGroupNo = approvalResignGroupNo ? Number(approvalResignGroupNo) : undefined
    const resignIsFixedGroup = approvalResignIsFixedGroup === "" ? undefined : (approvalResignIsFixedGroup as "T" | "F")

    const success = await performApprovalChange(selectedUserIds, approved, {
      employeeTypeId,
      customerGroupNo,
      employeeIsFixedGroup,
      employeeBaseField,
      resignGroupNo,
      resignIsFixedGroup,
    })
    if (approved && success) {
      setApprovalDialogOpen(false)
      resetApprovalForm()
    }
  }, [
    approvalCustomerGroupNo,
    approvalEmployeeBaseField,
    approvalEmployeeIsFixedGroup,
    approvalEmployeeTypeId,
    approvalResignGroupNo,
    approvalResignIsFixedGroup,
    ensureSelection,
    performApprovalChange,
    resetApprovalForm,
    selectedUserIds,
  ])

  const handleOpenApprovalDialog = useCallback(() => {
    if (!ensureSelection()) return
    resetApprovalForm()
    setApprovalDialogOpen(true)
  }, [ensureSelection, resetApprovalForm])

  const handleOpenApprovalDialogForUser = useCallback((userId: number) => {
    const targetUserIds = [userId]
    if (!ensureSelection(targetUserIds)) return

    resetApprovalForm()

    const targetUser = users.find((user) => user.id === userId)
    if (targetUser) {
      setApprovalEmployeeTypeId(targetUser.allowedEmployeeTypeId ? targetUser.allowedEmployeeTypeId.toString() : "")
      setApprovalCustomerGroupNo(targetUser.allowedEmployeeGroupNo ? targetUser.allowedEmployeeGroupNo.toString() : "")
      setApprovalEmployeeIsFixedGroup(
        typeof targetUser.allowedEmployeeIsFixedGroup === "boolean"
          ? targetUser.allowedEmployeeIsFixedGroup
            ? "T"
            : "F"
          : ""
      )
      setApprovalEmployeeBaseField(targetUser.allowedEmployeeBaseField ?? "")
      setApprovalResignGroupNo(targetUser.allowedResignGroupNo ? targetUser.allowedResignGroupNo.toString() : "")
      setApprovalResignIsFixedGroup(
        typeof targetUser.allowedResignIsFixedGroup === "boolean"
          ? targetUser.allowedResignIsFixedGroup
            ? "T"
            : "F"
          : ""
      )
    }

    setSelectedUserIds(targetUserIds)
    setApprovalDialogOpen(true)
  }, [ensureSelection, resetApprovalForm, users])

  const handleApprovalDialogOpenChange = useCallback((open: boolean) => {
    if (!open) {
      if (isActionLoading) return
      setApprovalDialogOpen(false)
      resetApprovalForm()
    }
  }, [isActionLoading, resetApprovalForm])

  const performSuperAdminChange = useCallback(async (targetUserIds: number[], promote: boolean) => {
    if (targetUserIds.length === 0) return

    const filteredUserIds = selfUserId
      ? targetUserIds.filter((userId) => userId !== selfUserId)
      : targetUserIds

    if (filteredUserIds.length === 0) {
      toast.error("본인 계정은 수정할 수 없습니다.")
      return
    }

    if (filteredUserIds.length !== targetUserIds.length) {
      toast.error("선택한 계정 중 본인 계정은 수정할 수 없습니다.")
    }

    const confirmMessage = promote
      ? `선택된 ${filteredUserIds.length}개 계정 - 마스터 계정으로 변경하시겠습니까?\n마스터 계정은 해당 메뉴 관리가 가능합니다.`
      : `선택된 ${filteredUserIds.length}개 계정 - 일반 계정으로 변경하시겠습니까?`
    if (!window.confirm(confirmMessage)) {
      return
    }

    setIsActionLoading(true)
    try {
      const payload: UsersUpdate = {
        users: filteredUserIds.map((userId) => ({
          userId,
          isSuperAdmin: promote ? "T" : "F",
          ...(promote ? { isApproved: "T" } : {}),
        })),
      }
      const response = await updateUsers(payload)
      if (response.code === 200) {
        toast.success(`선택한 ${filteredUserIds.length}개 계정이 ${promote ? "마스터 계정으로 변경" : "일반 계정으로 변경"}되었습니다.`)
        await refreshCurrentPage()
      } else {
        toast.error(response.message || "계정 권한 변경에 실패했습니다.")
      }
    } catch (error) {
      console.error("계정 권한 변경 오류:", error)
      toast.error("계정 권한 변경 중 오류가 발생했습니다.")
    } finally {
      setIsActionLoading(false)
    }
  }, [refreshCurrentPage, selfUserId])

  const handlePromoteSuperAdmin = useCallback(async () => {
    if (!ensureSelection()) return
    await performSuperAdminChange(selectedUserIds, true)
  }, [ensureSelection, performSuperAdminChange, selectedUserIds])

  const handleDemoteSuperAdmin = useCallback(async () => {
    if (!ensureSelection()) return
    await performSuperAdminChange(selectedUserIds, false)
  }, [ensureSelection, performSuperAdminChange, selectedUserIds])

  const performDelete = useCallback(async (targetUserIds: number[]) => {
    if (targetUserIds.length === 0) return

    const filteredUserIds = selfUserId
      ? targetUserIds.filter((userId) => userId !== selfUserId)
      : targetUserIds

    if (filteredUserIds.length === 0) {
      toast.error("본인 계정은 삭제할 수 없습니다.")
      return
    }

    if (filteredUserIds.length !== targetUserIds.length) {
      toast.error("선택한 계정 중 본인 계정은 삭제할 수 없습니다.")
    }

    const confirmMessage = `선택된 ${filteredUserIds.length}개 계정 - 삭제하시겠습니까?`
    if (!window.confirm(confirmMessage)) {
      return
    }

    setIsActionLoading(true)
    try {
      const response = await deleteUsers(filteredUserIds.join(","))
      if (response.code === 200) {
        toast.success(`선택한 ${filteredUserIds.length}개 계정이 삭제되었습니다.`)
        const isLastPageEmpty = filteredUserIds.length === users.length && page > 1
        const nextPage = isLastPageEmpty ? page - 1 : page
        if (isLastPageEmpty) {
          setPage(nextPage)
          await fetchUserList(nextPage, pageSize)
        } else {
          await refreshCurrentPage()
        }
      } else {
        toast.error(response.message || "계정 삭제에 실패했습니다.")
      }
    } catch (error) {
      console.error("계정 삭제 오류:", error)
      toast.error("계정 삭제 중 오류가 발생했습니다.")
    } finally {
      setIsActionLoading(false)
    }
  }, [fetchUserList, page, pageSize, refreshCurrentPage, selfUserId, users.length])

  const handleDelete = useCallback(async () => {
    if (!ensureSelection()) return
    await performDelete(selectedUserIds)
  }, [ensureSelection, performDelete, selectedUserIds])

  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      setSelectedUserIds(manageableUsers.map((user) => user.id))
    } else {
      setSelectedUserIds([])
    }
  }, [manageableUsers])

  const handleSelectUser = useCallback((userId: number, checked: boolean) => {
    if (selfUserId && userId === selfUserId) {
      toast.error("본인 계정은 선택할 수 없습니다.")
      return
    }
    setSelectedUserIds((prev) => {
      if (checked) {
        if (prev.includes(userId)) return prev
        return [...prev, userId]
      }
      return prev.filter((id) => id !== userId)
    })
  }, [selfUserId])

  const handlePageChange = useCallback((direction: "prev" | "next") => {
    if (direction === "prev" && page > 1) {
      setPage((prev) => prev - 1)
    } else if (direction === "next" && page < totalPages) {
      setPage((prev) => prev + 1)
    }
  }, [page, totalPages])

  const handlePageSizeChange = useCallback((value: string) => {
    const nextPageSize = parseInt(value, 10)
    setPageSize(nextPageSize)
    setPage(1)
  }, [])

  const formatDateTime = useCallback((timestamp?: string) => {
    if (!timestamp) return { date: "-", time: "" }
    const date = new Date(timestamp)
    if (Number.isNaN(date.getTime())) return { date: "-", time: "" }
    return {
      date: date.toLocaleDateString("ko-KR"),
      time: date.toLocaleTimeString("ko-KR", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }),
    }
  }, [])

  const renderStatusBadge = useCallback((status: boolean, trueLabel: string, falseLabel: string) => {
    return (
      <span className={status ? "text-foreground" : "text-[#E30000]"}>
        {status ? trueLabel : falseLabel}
      </span>
    )
  }, [])

  const renderLoadingState = useMemo(() => (
    <TableRow>
      <TableCell colSpan={11} className="h-32 text-center">
        <div className="flex items-center justify-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          데이터를 불러오는 중입니다...
        </div>
      </TableCell>
    </TableRow>
  ), [])

  if (authLoading && !authUser) {
    return (
      <div className="flex h-[60vh] flex-col items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
        사용자 정보를 불러오는 중입니다...
      </div>
    )
  }

  if (authUser && !authUser.isSuperAdmin) {
    return <Navigate to={ROUTES.DASHBOARD.EMPLOYEE.LIST} replace />
  }

  if (!authUser) {
    return null
  }

  return (
    <>
      <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">사용 계정 관리</h2>
        <p className="text-muted-foreground text-sm mt-3">
          마스터 계정은 임직원 관리 앱에 가입한 계정을 승인하여 앱 접속 여부 관리가 가능합니다. <br />
          해당 메뉴는 마스터 계정에서만 설정이 가능합니다.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>임직원 관리 앱 - 계정 목록</CardTitle>
          <CardDescription>총 {totalCount}개의 계정이 등록되어 있습니다.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <div className="text-sm text-muted-foreground">선택된 계정</div>
              <Button
                variant="outline"
                onClick={handleOpenApprovalDialog}
                disabled={isActionLoading || isLoading}
              >
                승인
              </Button>
              <Button
                variant="outline"
                onClick={() => handleApprovalChange(false)}
                disabled={isActionLoading || isLoading}
              >
                미승인
              </Button>
              <Button
                variant="outline"
                onClick={handleDelete}
                disabled={
                  isActionLoading ||
                  isLoading ||
                  (selectedUserIds.length > 0 && selectedSuperAdminCount > 0 && selectedSuperAdminCount === superAdminCount)
                }
              >
                삭제하기
              </Button>
            </div>

            <div className="flex items-center gap-3">
              <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
                <SelectTrigger className="w-33">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <SelectItem key={size} value={size}>
                      {size}개씩 보기
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12 text-center">
                    <Checkbox
                      checked={isAllSelected}
                      disabled={manageableUsers.length === 0}
                      onCheckedChange={(checked) => handleSelectAll(checked === true)}
                      ref={(ref) => {
                        if (ref) {
                          const checkbox = ref.querySelector('input[type="checkbox"]') as HTMLInputElement | null
                          if (checkbox) {
                            checkbox.indeterminate = isIndeterminate
                          }
                        }
                      }}
                    />
                  </TableHead>
                  <TableHead className="text-center">번호</TableHead>
                  <TableHead className="text-center">계정 타입</TableHead>
                  <TableHead className="text-center">승인 상태</TableHead>
                  <TableHead className="text-center">구분 권한</TableHead>  
                  <TableHead className="text-center">등록시 등급</TableHead>
                  <TableHead className="text-center">삭제시 등급</TableHead>
                  <TableHead className="text-center">이메일</TableHead>
                  <TableHead className="text-center">가입 일시</TableHead>
                  <TableHead className="text-center">승인 일시</TableHead>
                  <TableHead className="text-center">미승인 일시</TableHead>
                  <TableHead className="text-center">관리</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  renderLoadingState
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="py-10 text-center text-muted-foreground">
                      등록된 계정이 없습니다.
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user, index) => {
                    const isChecked = selectedUserIds.includes(user.id)
                    const employeeTypeName = user.isSuperAdmin ? "전체" : employeeTypeMap.get(user.allowedEmployeeTypeId)
                    const customerGroupName = user.isSuperAdmin ? "전체" : customerGroupMap.get(user.allowedEmployeeGroupNo)
                    const resignGroupNoName = user.isSuperAdmin ? "전체" : customerGroupMap.get(user.allowedResignGroupNo)
                    
                    return (
                      <TableRow key={user.id}>
                        <TableCell className="text-center">
                          <Checkbox
                            checked={isChecked}
                            disabled={user.id === selfUserId}
                            onCheckedChange={(checked) => handleSelectUser(user.id, checked === true)}
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          {totalCount - ((page - 1) * pageSize + index)}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant={user.isSuperAdmin ? "warning" : "success"}>
                            {user.isSuperAdmin ? "마스터" : "일반"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          {renderStatusBadge(user.isApproved, "승인", "미승인")}
                        </TableCell>
                        <TableCell className="text-center">
                          {employeeTypeName ?? "-"}
                        </TableCell>
                        <TableCell className="text-center">
                          {customerGroupName ?? "-"}
                        </TableCell>
                        <TableCell className="text-center">
                          {resignGroupNoName ?? "-"}
                        </TableCell>
                        <TableCell className="text-center">{user.email}</TableCell>
                        {(() => {
                          const { date, time } = formatDateTime(user.createdAt)
                          return (
                            <TableCell className="text-center">
                              {date}
                              {time && (
                                <>
                                  <br />
                                  <span className="text-xs text-muted-foreground">{time}</span>
                                </>
                              )}
                            </TableCell>
                          )
                        })()}
                        {(() => {
                          const { date, time } = formatDateTime(user.approvedAt)
                          return (
                            <TableCell className="text-center">
                              {date}
                              {time && (
                                <>
                                  <br />
                                  <span className="text-xs text-muted-foreground">{time}</span>
                                </>
                              )}
                            </TableCell>
                          )
                        })()}
                        {(() => {
                          const { date, time } = formatDateTime(user.unapprovedAt)
                          return (
                            <TableCell className="text-center">
                              {date}
                              {time && (
                                <>
                                  <br />
                                  <span className="text-xs text-muted-foreground">{time}</span>
                                </>
                              )}
                            </TableCell>
                          )
                        })()}
                        <TableCell className="text-center">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 p-0"
                                disabled={user.id === selfUserId}
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {user.isSuperAdmin ? (
                                <>
                                  <DropdownMenuItem
                                    className="cursor-pointer"
                                    onClick={() => performSuperAdminChange([user.id], false)}
                                    disabled={
                                      user.id === selfUserId ||
                                      (superAdminCount > 0 && superAdminCount === 1 && user.isSuperAdmin)
                                    }
                                  >
                                    일반 계정 변경
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className="cursor-pointer text-destructive focus:text-destructive"
                                    onClick={() => performDelete([user.id])}
                                    disabled={
                                      user.id === selfUserId ||
                                      (superAdminCount > 0 && superAdminCount === 1 && user.isSuperAdmin)
                                    }
                                  >
                                    삭제
                                  </DropdownMenuItem>
                                </>
                              ) : (
                                <>
                                  <DropdownMenuItem
                                    className="cursor-pointer"
                                    onClick={() => handleOpenApprovalDialogForUser(user.id)}
                                    disabled={isActionLoading || isLoading}
                                  >
                                    계정 권한 설정
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className="cursor-pointer"
                                    onClick={() => performSuperAdminChange([user.id], true)}
                                    disabled={user.id === selfUserId}
                                  >
                                    마스터 계정 변경
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className="cursor-pointer text-destructive focus:text-destructive"
                                    onClick={() => performDelete([user.id])}
                                    disabled={user.id === selfUserId}
                                  >
                                    삭제
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <div className="text-sm text-muted-foreground">선택된 계정</div>
              <Button
                variant="outline"
                onClick={handleOpenApprovalDialog}
                disabled={isActionLoading || isLoading}
              >
                승인
              </Button>
              <Button
                variant="outline"
                onClick={() => handleApprovalChange(false)}
                disabled={isActionLoading || isLoading}
              >
                미승인
              </Button>
              <Button
                variant="outline"
                onClick={handleDelete}
                disabled={
                  isActionLoading ||
                  isLoading ||
                  (selectedUserIds.length > 0 && selectedSuperAdminCount > 0 && selectedSuperAdminCount === superAdminCount)
                }
              >
                삭제하기
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange("prev")}
                disabled={page === 1 || isLoading}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-muted-foreground">
                {page} / {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange("next")}
                disabled={page === totalPages || isLoading}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      </div>
      <Dialog open={approvalDialogOpen} onOpenChange={handleApprovalDialogOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>계정 권한 설정</DialogTitle>
          <DialogDescription>
              해당 계정이 볼 수 있는 임직원 구분과 <br />
              등록 가능한 회원 등급을 설정합니다.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {selectedUserIds.length > 1 && (
            <div className="text-sm text-muted-foreground">
              선택된 계정 수: {selectedUserIds.length}개
            </div>
          )}
          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">임직원 구분 권한</p>
            <Select
              value={approvalEmployeeTypeId}
              onValueChange={setApprovalEmployeeTypeId}
              disabled={employeeTypes.length === 0 || isActionLoading}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="구분 권한을 선택해주세요" />
              </SelectTrigger>
              <SelectContent>
                {employeeTypes.map((type) => (
                  <SelectItem key={type.typeId} value={type.typeId.toString()}>
                    {type.typeName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {employeeTypes.length === 0 && (
              <p className="text-xs text-muted-foreground">
                설정 가능한 관리 구분이 없습니다. 설정에서 구분을 먼저 등록해주세요.
              </p>
            )}
          </div>
          
          <div className="h-px bg-border my-6" />

          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">임직원 회원 정보 등록 / 임직원 회원 등급 일괄 변경</p>
            <Select
              value={approvalCustomerGroupNo}
              onValueChange={setApprovalCustomerGroupNo}
              disabled={customerGroups.length === 0 || isActionLoading}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="회원 등급을 선택" />
              </SelectTrigger>
              <SelectContent>
                {customerGroups.map((group) => (
                  <SelectItem key={group.groupNo} value={group.groupNo.toString()}>
                    {group.groupName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {customerGroups.length === 0 && (
              <p className="text-xs text-muted-foreground">
                설정 가능한 회원 등급이 없습니다. 셀러 센터에서 회원 등급을 먼저 등록해주세요.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Select
              value={approvalEmployeeIsFixedGroup}
              onValueChange={(value) => setApprovalEmployeeIsFixedGroup(value as "T" | "F")}
              disabled={isActionLoading}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="회원 매칭시 등급 고정 여부 선택" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="T">회원 등급 고정함</SelectItem>
                <SelectItem value="F">회원 등급 고정안함</SelectItem>
              </SelectContent>
              </Select>
          </div>
          <div className="space-y-2">
            <Select
              value={approvalEmployeeBaseField}
              onValueChange={(value) => setApprovalEmployeeBaseField(value as "email" | "phoneNumber")}
              disabled={isActionLoading}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="회원 정보 매칭 방법 선택" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="phoneNumber">휴대폰 정보 매칭</SelectItem>
                <SelectItem value="email">이메일 정보 매칭</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="h-px bg-border my-6" />

          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">일반 회원 등급 일괄 변경 (퇴사 및 제휴 종료등)</p>
            <Select
              value={approvalResignGroupNo}
              onValueChange={setApprovalResignGroupNo}
              disabled={customerGroups.length === 0 || isActionLoading}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="회원 등급 선택" />
              </SelectTrigger>
              <SelectContent>
                {customerGroups.map((group) => (
                  <SelectItem key={group.groupNo} value={group.groupNo.toString()}>
                    {group.groupName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {customerGroups.length === 0 && (
              <p className="text-xs text-muted-foreground">
                설정 가능한 회원 등급이 없습니다. 셀러 센터에서 회원 등급을 먼저 등록해주세요.
              </p>
            )}
          </div>

          <Select
            value={approvalResignIsFixedGroup}
            onValueChange={(value) => setApprovalResignIsFixedGroup(value as "T" | "F")}
            disabled={isActionLoading}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="회원 매칭시 등급 고정 여부 선택" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="T">회원 등급 고정함</SelectItem>
              <SelectItem value="F">회원 등급 고정안함</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setApprovalDialogOpen(false)
              resetApprovalForm()
            }}
            disabled={isActionLoading}
          >
            취소
          </Button>
          <Button
            onClick={() => handleApprovalChange(true)}
            disabled={
              isActionLoading ||
              !approvalEmployeeTypeId ||
              !approvalCustomerGroupNo ||
              !approvalEmployeeIsFixedGroup ||
              !approvalEmployeeBaseField ||
              !approvalResignGroupNo ||
              !approvalResignIsFixedGroup
            }
          >
            {isActionLoading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                등록 중...
              </span>
            ) : (
              "등록"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
      </Dialog>
    </>
  )
}

export default UsersManagementPage
